import {
  getBulkRowTuples,
  projectBulkStatusEvent,
} from "./bulkUploadProjection";
import { persistBulkStatusProjection } from "./bulkUploadPersistence";

const currentScope = (isCurrent) => {
  try {
    return typeof isCurrent !== "function" || isCurrent();
  } catch (error) {
    return false;
  }
};

export const createBulkUploadStatusAdapter = ({
  project = projectBulkStatusEvent,
  persist = persistBulkStatusProjection,
  publish,
} = {}) => {
  if (typeof project !== "function") {
    throw new TypeError("bulk projector must be a function");
  }
  if (typeof persist !== "function") {
    throw new TypeError("bulk persistence must be a function");
  }
  if (typeof publish !== "function") {
    throw new TypeError("bulk publisher must be a function");
  }
  let active = true;
  let writes = null;
  const durableRows = new Map();

  const rowKey = (row) => getBulkRowTuples(row).join(",");

  const apply = ({ getRows, event, isCurrent } = {}) => {
    if (!active || !currentScope(isCurrent) || typeof getRows !== "function") {
      return Promise.resolve({ outcome: "ignored" });
    }
    const execute = async () => {
      if (!active || !currentScope(isCurrent)) return { outcome: "ignored" };
      let projection;
      try {
        const liveRows = getRows();
        const sourceRows = Array.isArray(liveRows)
          ? liveRows.map((row) => {
              const durableRow = durableRows.get(rowKey(row));
              return durableRow
                ? { ...row, status: durableRow.status }
                : row;
            })
          : liveRows;
        projection = project(sourceRows, event);
      } catch (error) {
        return { outcome: "fallback" };
      }
      if (projection?.outcome !== "applied") {
        return projection?.outcome === "ignored"
          ? { outcome: "ignored" }
          : { outcome: "fallback" };
      }
      try {
        const persisted = await persist({
          row: projection.row,
          publish,
          isCurrent: () => active && currentScope(isCurrent),
        });
        if (!persisted || !active || !currentScope(isCurrent)) {
          return { outcome: "ignored" };
        }
        durableRows.set(rowKey(projection.row), projection.row);
      } catch (error) {
        return active && currentScope(isCurrent)
          ? { outcome: "fallback" }
          : { outcome: "ignored" };
      }
      return { ...projection, outcome: "applied" };
    };

    const operation = writes ? writes.catch((error) => { console.warn("bulkUploadStatusAdapter.js queue continuation suppressed previous failure", error); }).then(execute) : execute();
    writes = operation;
    return operation;
  };

  const cleanup = () => {
    active = false;
    durableRows.clear();
  };

  return { apply, cleanup };
};

export default createBulkUploadStatusAdapter;
